import {
  ArrowLeft,
  Building2,
  Calendar,
  Copy,
  ExternalLink,
  Hash,
  RefreshCw,
  Settings,
  Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { api, getApiErrorMessage } from '../../api/client';
import type {
  CargoCompanyRow,
  CargoCompanyStats,
  CargoEmployeeRow,
  CargoInvoiceRow,
  Pagination,
} from '../../api/types';
import { config } from '../../config';
import { useAuth } from '../../context/AuthContext';
import {
  companyStatusBadge,
  companyStatusLabel,
  formatCountryLabel,
  formatDateTime,
  formatUsdt,
  formatUsdtVolume,
  invoicePaymentUrl,
  invoiceStatusBadge,
  invoiceStatusLabel,
  splitStatusBadge,
  splitStatusLabel,
  tronscanTxUrl,
} from '../../utils/cargoStatus';
import { canManageCargo, canSupportCargo } from '../../utils/roles';
import { CargoInvoiceDetailModal } from './CargoInvoiceDetailModal';

type Props = {
  companyId: string;
  onBack: () => void;
};

export function CargoCompanyDetail({ companyId, onBack }: Props) {
  const { role } = useAuth();
  const canManage = canManageCargo(role);
  const canSupport = canSupportCargo(role);

  const [company, setCompany] = useState<CargoCompanyRow | null>(null);
  const [employees, setEmployees] = useState<CargoEmployeeRow[]>([]);
  const [stats, setStats] = useState<CargoCompanyStats | null>(null);
  const [invoices, setInvoices] = useState<CargoInvoiceRow[]>([]);
  const [invoicePagination, setInvoicePagination] = useState<Pagination | null>(null);
  const [tab, setTab] = useState<'employees' | 'invoices' | 'settings'>('employees');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [invoicePage, setInvoicePage] = useState(1);
  const [selectedInvoice, setSelectedInvoice] = useState<string | null>(null);

  const [editName, setEditName] = useState('');
  const [editWallet, setEditWallet] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async (invPage = invoicePage) => {
    setLoading(true);
    try {
      const [co, inv] = await Promise.all([
        api.getCargoCompany(companyId),
        api.listCargoInvoices({ company_id: companyId, page: invPage, limit: 50 }),
      ]);
      setCompany(co.company);
      setEmployees(co.employees);
      setStats(co.stats);
      setInvoices(inv.invoices);
      setInvoicePagination(inv.pagination);
      setEditName(co.company.name);
      setEditWallet(co.company.tron_payout_wallet || '');
      setEditNotes(co.company.internal_notes || '');
      setError(null);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load(1);
  }, [companyId]);

  useEffect(() => {
    if (invoicePage > 1) void load(invoicePage);
  }, [invoicePage]);

  const copyText = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      /* ignore */
    }
  };

  const toggleStatus = async () => {
    if (!company || !canManage) return;
    let next: 'active' | 'suspended';
    if (company.status === 'pending') {
      next = 'active';
    } else if (company.status === 'active') {
      next = 'suspended';
    } else {
      next = 'active';
    }
    const verb =
      company.status === 'pending'
        ? 'Approve'
        : next === 'suspended'
          ? 'Suspend'
          : 'Activate';
    if (!window.confirm(`${verb} ${company.name}?`)) return;
    try {
      const res = await api.patchCargoCompany(companyId, { status: next });
      setCompany(res.company);
    } catch (e) {
      setError(getApiErrorMessage(e));
    }
  };

  const saveSettings = async () => {
    if (!canManage) return;
    setSaving(true);
    try {
      const res = await api.patchCargoCompany(companyId, {
        name: editName.trim(),
        tron_payout_wallet: editWallet.trim() || null,
        internal_notes: editNotes.trim() || null,
      });
      setCompany(res.company);
      setError(null);
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const patchEmployee = async (
    memberId: string,
    patch: { status?: 'active' | 'disabled'; role?: 'admin' | 'manager' },
  ) => {
    try {
      await api.patchCargoEmployee(companyId, memberId, patch);
      await load(invoicePage);
    } catch (e) {
      setError(getApiErrorMessage(e));
    }
  };

  const resetPassword = async (memberId: string, email: string) => {
    const pwd = window.prompt(`New password for ${email}`);
    if (!pwd) return;
    try {
      await api.resetCargoEmployeePassword(companyId, memberId, pwd);
      window.alert('Password updated');
    } catch (e) {
      setError(getApiErrorMessage(e));
    }
  };

  if (loading && !company) {
    return <div className="cargo-loading-card card">Loading partner…</div>;
  }

  if (!company) {
    return (
      <div className="cargo-loading-card card">
        Partner not found
        <button type="button" className="btn-outline btn-inline" onClick={onBack}>
          Back
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="cargo-detail-toolbar">
        <button type="button" className="btn-outline btn-inline" onClick={onBack}>
          <ArrowLeft size={16} aria-hidden />
          Back to partners
        </button>
        <button type="button" className="btn-outline btn-inline" onClick={() => void load(invoicePage)}>
          <RefreshCw size={16} aria-hidden />
          Refresh
        </button>
        {canManage ? (
          <button type="button" className="btn-outline btn-inline" onClick={() => void toggleStatus()}>
            {company.status === 'pending'
              ? 'Approve partner'
              : company.status === 'active'
                ? 'Suspend partner'
                : 'Activate partner'}
          </button>
        ) : null}
      </div>

      {error ? <p className="form-error section-error">{error}</p> : null}

      <div className="cargo-detail-hero card">
        <div className="cargo-detail-hero-icon">
          <Building2 size={22} color="var(--onyx-gold)" aria-hidden />
        </div>
        <div className="cargo-detail-hero-body">
          <div className="cargo-detail-title-row">
            <h1 className="cargo-detail-title">{company.name}</h1>
            <span className={companyStatusBadge(company.status)}>{companyStatusLabel(company.status)}</span>
          </div>
          <div className="cargo-detail-meta">
            <span className="cargo-meta-chip">
              <Hash size={12} aria-hidden />
              <code>{company.company_code}</code>
              <button type="button" className="cargo-copy-btn" onClick={() => void copyText(company.company_code, 'code')}>
                <Copy size={12} />
              </button>
              {copied === 'code' ? <span className="cargo-copied">Copied</span> : null}
            </span>
            <span className="cargo-meta-chip">
              <Calendar size={12} aria-hidden />
              Joined {formatDateTime(company.created_at)}
            </span>
            {company.tron_payout_wallet ? (
              <span className="cargo-meta-chip cargo-wallet-chip">
                <code>{company.tron_payout_wallet}</code>
              </span>
            ) : (
              <span className="cargo-meta-chip cargo-wallet-missing">No payout wallet</span>
            )}
          </div>
        </div>
        <div className="cargo-detail-mini-stats">
          <div className="cargo-mini-stat">
            <span className="cargo-mini-label">Employees</span>
            <span className="cargo-mini-value">
              {employees.length}
              <span className="cargo-mini-unit">users</span>
            </span>
          </div>
          <div className="cargo-mini-stat">
            <span className="cargo-mini-label">Invoices</span>
            <span className="cargo-mini-value">
              {stats?.invoice_count ?? '0'}
              <span className="cargo-mini-unit">total</span>
            </span>
          </div>
          <div className="cargo-mini-stat">
            <span className="cargo-mini-label">Volume</span>
            <span className="cargo-mini-value">
              {formatUsdtVolume(stats?.volume_usdt)}
              <span className="cargo-mini-unit">USDT</span>
            </span>
          </div>
          <div className="cargo-mini-stat">
            <span className="cargo-mini-label">Commission</span>
            <span className="cargo-mini-value cargo-mini-paid">
              {formatUsdtVolume(stats?.commission_usdt)}
              <span className="cargo-mini-unit">USDT</span>
            </span>
          </div>
          <div className="cargo-mini-stat">
            <span className="cargo-mini-label">Split failed</span>
            <span
              className={`cargo-mini-value${Number.parseInt(stats?.split_failed_count ?? '0', 10) > 0 ? ' cargo-mini-warn' : ''}`}
            >
              {stats?.split_failed_count ?? '0'}
              <span className="cargo-mini-unit">invoices</span>
            </span>
          </div>
        </div>
      </div>

      <div className="card cargo-profile-card">
        <h2 className="cargo-profile-title">Registration profile</h2>
        <div className="cargo-detail-list">
          <div className="cargo-detail-row">
            <span>Website</span>
            <span>
              {company.website ? (
                <a href={company.website} target="_blank" rel="noopener noreferrer">
                  {company.website}
                </a>
              ) : (
                '—'
              )}
            </span>
          </div>
          <div className="cargo-detail-row">
            <span>Country</span>
            <span>
              {company.registration_country
                ? `${formatCountryLabel(company.registration_country)} (${company.registration_country})`
                : '—'}
            </span>
          </div>
          <div className="cargo-detail-row">
            <span>Admin email</span>
            <span>{employees.find((e) => e.role === 'admin')?.email ?? employees[0]?.email ?? '—'}</span>
          </div>
          <div className="cargo-detail-row">
            <span>Admin name</span>
            <span>{employees.find((e) => e.role === 'admin')?.full_name ?? employees[0]?.full_name ?? '—'}</span>
          </div>
          <div className="cargo-detail-row">
            <span>Tron payout wallet</span>
            <span>{company.tron_payout_wallet ? <code>{company.tron_payout_wallet}</code> : '—'}</span>
          </div>
          <div className="cargo-detail-row">
            <span>Commission</span>
            <span>
              {company.commission_bps != null ? `${(company.commission_bps / 100).toFixed(2)}% (${company.commission_bps} bps)` : 'Default'}
            </span>
          </div>
          <div className="cargo-detail-row">
            <span>Partner code</span>
            <span><code>{company.company_code}</code></span>
          </div>
          <div className="cargo-detail-row">
            <span>Registered</span>
            <span>{formatDateTime(company.created_at)}</span>
          </div>
          {company.internal_notes ? (
            <div className="cargo-detail-row">
              <span>Internal notes</span>
              <span className="cargo-notes-preview">{company.internal_notes}</span>
            </div>
          ) : null}
        </div>
      </div>

      <div className="profile-tabs cargo-tabs">
        <button type="button" className={`profile-tab ${tab === 'employees' ? 'active' : ''}`} onClick={() => setTab('employees')}>
          <Users size={14} style={{ marginRight: 6, verticalAlign: -2 }} aria-hidden />
          Employees ({employees.length})
        </button>
        <button type="button" className={`profile-tab ${tab === 'invoices' ? 'active' : ''}`} onClick={() => setTab('invoices')}>
          Invoices ({stats?.invoice_count ?? invoices.length})
        </button>
        {canManage ? (
          <button type="button" className={`profile-tab ${tab === 'settings' ? 'active' : ''}`} onClick={() => setTab('settings')}>
            <Settings size={14} style={{ marginRight: 6, verticalAlign: -2 }} aria-hidden />
            Settings
          </button>
        ) : null}
      </div>

      {tab === 'employees' ? (
        <div className="table-wrap cargo-table">
          <div className="table-head data-table-head cargo-employees-head cargo-employees-head-ext">
            <div className="th">Email</div>
            <div className="th">Name</div>
            <div className="th">Role</div>
            <div className="th">Status</div>
            <div className="th">Joined</div>
            {canSupport ? <div className="th">Actions</div> : null}
          </div>
          {employees.length === 0 ? (
            <div className="cargo-empty-row">No employees</div>
          ) : (
            employees.map((e) => (
              <div key={e.id} className="table-row data-table-row cargo-employees-row cargo-employees-row-ext">
                <div className="cell-primary">{e.email}</div>
                <div>{e.full_name || '—'}</div>
                <div>
                  {canSupport ? (
                    <select
                      className="cargo-role-select"
                      value={e.role}
                      onChange={(ev) =>
                        void patchEmployee(e.id, { role: ev.target.value as 'admin' | 'manager' })
                      }
                    >
                      <option value="admin">admin</option>
                      <option value="manager">manager</option>
                    </select>
                  ) : (
                    <span className="cargo-role-pill">{e.role}</span>
                  )}
                </div>
                <div>
                  <span className={e.status === 'active' ? 'badge badge-success' : 'badge badge-blocked'}>
                    {e.status}
                  </span>
                </div>
                <div className="cell-muted">{formatDateTime(e.created_at)}</div>
                {canSupport ? (
                  <div className="cargo-link-actions">
                    <button
                      type="button"
                      className="btn-outline btn-sm"
                      onClick={() =>
                        void patchEmployee(e.id, {
                          status: e.status === 'active' ? 'disabled' : 'active',
                        })
                      }
                    >
                      {e.status === 'active' ? 'Disable' : 'Enable'}
                    </button>
                    <button
                      type="button"
                      className="btn-outline btn-sm"
                      onClick={() => void resetPassword(e.id, e.email)}
                    >
                      Reset pwd
                    </button>
                  </div>
                ) : null}
              </div>
            ))
          )}
        </div>
      ) : null}

      {tab === 'invoices' ? (
        <>
          <CargoInvoiceList
            rows={invoices}
            copied={copied}
            onCopy={copyText}
            onOpen={(hash) => setSelectedInvoice(hash)}
          />
          {invoicePagination && invoicePagination.pages > 1 ? (
            <div className="cargo-pagination">
              <button
                type="button"
                className="btn-outline btn-sm"
                disabled={invoicePage <= 1}
                onClick={() => setInvoicePage((p) => p - 1)}
              >
                Previous
              </button>
              <span className="cell-muted">
                Page {invoicePagination.page} / {invoicePagination.pages}
              </span>
              <button
                type="button"
                className="btn-outline btn-sm"
                disabled={invoicePage >= invoicePagination.pages}
                onClick={() => setInvoicePage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          ) : null}
        </>
      ) : null}

      {tab === 'settings' && canManage ? (
        <div className="card cargo-settings-card">
          <div className="form-stack">
            <label className="field-label">
              Company name
              <input className="inp" value={editName} onChange={(e) => setEditName(e.target.value)} />
            </label>
            <label className="field-label">
              Tron payout wallet
              <input className="inp" value={editWallet} onChange={(e) => setEditWallet(e.target.value)} placeholder="T…" />
            </label>
            <label className="field-label">
              Internal notes (staff only)
              <textarea
                className="inp cargo-notes"
                rows={4}
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
              />
            </label>
            <button type="button" className="btn-primary btn-inline" disabled={saving} onClick={() => void saveSettings()}>
              {saving ? 'Saving…' : 'Save settings'}
            </button>
          </div>
        </div>
      ) : null}

      <CargoInvoiceDetailModal
        hashId={selectedInvoice}
        open={Boolean(selectedInvoice)}
        onClose={() => setSelectedInvoice(null)}
        canManage={canManage}
        onUpdated={() => void load(invoicePage)}
      />
    </>
  );
}

type CargoInvoiceListProps = {
  rows: CargoInvoiceRow[];
  copied: string | null;
  onCopy: (text: string, key: string) => void;
  onOpen: (hashId: string) => void;
  showCompany?: boolean;
};

export function CargoInvoiceList({ rows, copied, onCopy, onOpen, showCompany = false }: CargoInvoiceListProps) {
  if (rows.length === 0) {
    return <div className="cargo-empty-row">No invoices</div>;
  }

  return (
    <div className="table-wrap cargo-table">
      <div className={`table-head data-table-head cargo-invoices-head ${showCompany ? '' : 'cargo-invoices-head-ext'}`}>
        <div className="th">Invoice</div>
        {showCompany ? <div className="th">Partner</div> : null}
        <div className="th">Order ID</div>
        <div className="th">Amount</div>
        <div className="th">Status</div>
        <div className="th">Split</div>
        <div className="th">Created</div>
        <div className="th">Link</div>
      </div>
      {rows.map((i) => {
        const payUrl = invoicePaymentUrl(i.hash_id, config.invoicePayBaseUrl);
        return (
          <div
            key={i.hash_id}
            className={`table-row data-table-row cargo-invoices-row ${showCompany ? '' : 'cargo-invoices-row-ext'} cargo-invoices-row-click`}
            onClick={() => onOpen(i.hash_id)}
            onKeyDown={(e) => e.key === 'Enter' && onOpen(i.hash_id)}
            role="button"
            tabIndex={0}
          >
            <div>
              <code className="cargo-hash">{i.hash_id}</code>
            </div>
            {showCompany ? (
              <div>
                <div className="cell-primary">{i.company_name || '—'}</div>
                {i.company_code ? <div className="cell-muted">{i.company_code}</div> : null}
              </div>
            ) : null}
            <div className="cell-muted">{i.cargo_id || '—'}</div>
            <div className="cargo-amount">{formatUsdt(i.amount)} USDT</div>
            <div>
              <span className={invoiceStatusBadge(i.status)}>{invoiceStatusLabel(i.status)}</span>
            </div>
            <div>
              <span className={splitStatusBadge(i.split_status)}>{splitStatusLabel(i.split_status)}</span>
            </div>
            <div className="cell-muted">{formatDateTime(i.created_at)}</div>
            <div className="cargo-link-actions" onClick={(e) => e.stopPropagation()}>
              <button type="button" className="btn-outline btn-sm btn-icon" onClick={() => void onCopy(payUrl, i.hash_id)}>
                <Copy size={13} />
              </button>
              <a href={payUrl} target="_blank" rel="noopener noreferrer" className="btn-outline btn-sm btn-icon">
                <ExternalLink size={13} />
              </a>
              {tronscanTxUrl(i.tx_hash) ? (
                <a href={tronscanTxUrl(i.tx_hash)!} target="_blank" rel="noopener noreferrer" className="btn-outline btn-sm btn-icon" title="Tronscan">
                  ⛓
                </a>
              ) : null}
              {copied === i.hash_id ? <span className="cargo-copied-inline">Copied</span> : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
